import { TradingStrategyDirection } from "../../api/hooks/useTradingStrategies";
import { AltSymbolIcon, DirectionIcon, RiskIcon } from "@scryerlabs/ui";
import { isHelpMarkType } from "./marks";

/**
 * A single strategy-card mark, inline in an article's prose.
 *
 * Slightly larger than on a card: it sits next to body text here rather than in
 * a dense grid, and it is the thing being explained rather than a detail of
 * something else.
 */
const SIZE = 22;

const DIRECTIONS: Record<string, number> = {
  "long-only": TradingStrategyDirection.LongOnly,
  "short-only": TradingStrategyDirection.ShortOnly,
  "long-and-short": TradingStrategyDirection.LongAndShort,
};

const RISK_LEVEL = /^risk-([1234])$/;

/**
 * The mark is the thing being explained here, so its own words are authored in the article and
 * arrive as `label`. These are the fallbacks for a mark written without one - the same English the
 * article would have used, rather than a blank tooltip.
 */
const FALLBACK_LABEL: Record<string, string> = {
  "alt-symbols": "Alt-Symbol Boost",
  "long-only": "Trades long only",
  "short-only": "Trades short only",
  "long-and-short": "Trades long and short",
};

export function HelpMark({ type, label }: { type?: string; label?: string }) {
  if (!type || !isHelpMarkType(type)) return null;

  const mark = () => {
    const marked = label ?? FALLBACK_LABEL[type] ?? "";

    if (type === "alt-symbols") return <AltSymbolIcon label={marked} size={SIZE} />;

    const risk = RISK_LEVEL.exec(type);
    // The level word is authored in the article rather than read from the
    // reference data: this is an explanation of all three, not a rendering of
    // any one strategy.
    if (risk) {
      return (
        <RiskIcon riskProfile={Number(risk[1])} label={marked} size={SIZE} />
      );
    }

    return <DirectionIcon direction={DIRECTIONS[type]} label={marked} size={SIZE} />;
  };

  return <span className="help-mark">{mark()}</span>;
}
